import { css } from '@emotion/react';
import React from 'react';

import { usePrepareSwitchingPlansQuery } from '$/GraphQL/schema/schema';
import EDate from '$/shared/frontend/utils/EDate';

import Loading from '$/components/Loading';
import SimpleModal from '$/components/ui/SimpleModal';
import { useThemeContext } from '$/components/ui/ThemeContext';
import { useHasChangedShallow } from '$/hooks/useHasChanged';
import { formatCurrency } from '$/utils/formatCurrency';
import { usePlanPageContext } from '../PlanPage';
import { SwitchingType } from '../SubscriptionInfo';
import { useSwitchPlans } from './useSwitchPlans';
import Button from '$/components/ui/Button';

export const SwitchingPlanModal: React.FC<{
  showModal: boolean;
  onCancel: () => void;
}> = ({ onCancel, showModal }) => {
  const etheme = useThemeContext();
  const { subscriptionInfo } = usePlanPageContext();
  const { submitting, error, switchPlans } = useSwitchPlans();
  const { data, loading } = usePrepareSwitchingPlansQuery({
    skip: !showModal,
    fetchPolicy: 'network-only',
  });

  const submittingChanged = useHasChangedShallow([submitting]);
  React.useEffect(() => {
    if (!submittingChanged || submitting || error) return;
    onCancel();
  }, [error, onCancel, submitting, submittingChanged]);

  const title =
    subscriptionInfo.switchingType === SwitchingType.AnnuallyToMonthly
      ? 'Switch to monthly subscription?'
      : 'Switch to annual subscription?';

  const content = React.useMemo(() => {
    if (loading || !data) {
      return <Loading local />;
    }
    if (subscriptionInfo.switchingType === SwitchingType.AnnuallyToMonthly) {
      return (
        <>
          <p>
            If you switch to a monthly subscription, the change will not go into effect and you will
            not be charged until your next billing date, on{' '}
            <b>{new EDate(data.prepareSwitchingPlans.currentPeriodEnd).format('P')}</b>, when your
            current annual subscription ends.
          </p>
          <p>Do you wish to continue?</p>
        </>
      );
    }

    return (
      <>
        <p>
          If you switch to a annual subscription, your credit card will be immediately charged
          <b> {data.prepareSwitchingPlans.additional}</b>, which is the full annual subscription
          rate of <b>{formatCurrency(data.prepareSwitchingPlans.nextPrice)}</b> minus credit for
          time remaining in this month's billing cycle. Your annual subscription will start today.
        </p>
        <p className='font-semibold'>Once charged, subscriptions are non-refundable.</p>
        <p> Do you wish to continue?</p>
      </>
    );
  }, [data, loading, subscriptionInfo.switchingType]);

  const buttonText = React.useMemo(() => {
    if (subscriptionInfo.switchingType === SwitchingType.AnnuallyToMonthly) {
      return <span>Switch to monthly</span>;
    }
    return !data?.prepareSwitchingPlans.additional ? (
      <span>Switch to annual</span>
    ) : (
      <span>
        Pay <b>{data.prepareSwitchingPlans.additional}</b> now & switch to annual
      </span>
    );
  }, [data?.prepareSwitchingPlans.additional, subscriptionInfo.switchingType]);

  return (
    <SimpleModal
      title={title}
      className='w-full md:w-auto md:min-w-40e'
      onCancel={onCancel}
      hide={!showModal}
      cancel={<Button className='mr-4'>Cancel</Button>}
      submit={
        <Button.submit
          theme={etheme.controls.button.action}
          css={css`
            font-weight: 400;
          `}
          disabled={!data || loading}
          onClick={(): void => void switchPlans()}
          submitting={submitting}
        >
          {buttonText}
        </Button.submit>
      }
    >
      {content}
      {error && (
        <p className='text-color-error mb-2'>
          {error} If this problem persists, please contact{' '}
          <a href='mailto://support@ewebinar.com'>support@ewebinar.com</a>.
        </p>
      )}
    </SimpleModal>
  );
};
